import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight } from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import Seo, { breadcrumbSchema } from "../components/Seo";

export default function Services() {
    const [services, setServices] = useState([]);
    const [search, setSearch] = useState("");
    const [params, setParams] = useSearchParams();
    const activeCat = params.get("cat") || "All";

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data || []));
    }, []);

    const categories = useMemo(() => {
        const set = new Set(services.map((s) => s.category));
        return ["All", ...Array.from(set)];
    }, [services]);

    const filtered = useMemo(() => {
        return services.filter((s) => {
            const matchCat = activeCat === "All" || s.category === activeCat;
            const matchSearch =
                !search ||
                s.name.toLowerCase().includes(search.toLowerCase()) ||
                s.short_desc.toLowerCase().includes(search.toLowerCase());
            return matchCat && matchSearch;
        });
    }, [services, activeCat, search]);

    const setCat = (c) => {
        const p = new URLSearchParams(params);
        if (c === "All") p.delete("cat");
        else p.set("cat", c);
        setParams(p);
    };

    // group by category for clean display when "All"
    const grouped = useMemo(() => {
        if (activeCat !== "All") return { [activeCat]: filtered };
        const g = {};
        for (const s of filtered) {
            (g[s.category] = g[s.category] || []).push(s);
        }
        return g;
    }, [filtered, activeCat]);

    return (
        <div data-testid="services-page">
            <Seo
                title="Aesthetic Treatments & Wellness Services — San Diego"
                description="Explore IV therapy, GLP-1 weight loss, hair restoration, peptides, neurotoxins, and aesthetic injectables at Zenith Wellness in San Diego. Clinician-led, results-driven, refined."
                path="/services"
                keywords="aesthetic treatments San Diego, IV therapy menu, GLP-1, semaglutide, NAD+, glutathione, Botox San Diego, hair restoration, peptides"
                schema={breadcrumbSchema([
                    { name: "Home", path: "/" },
                    { name: "Treatments", path: "/services" },
                ])}
            />
            {/* HEADER */}
            <section className="bg-[#262422] text-[#F2EDE2]">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-36 text-center">
                    <div className="eyebrow text-[#A89070] mb-6">All Treatments</div>
                    <h1 className="serif text-6xl md:text-7xl lg:text-8xl leading-[1.02]">
                        Medical aesthetics,<br />
                        <span className="italic text-[#A89070]">curated for you.</span>
                    </h1>
                    <p className="mt-8 text-lg text-[#F2EDE2]/75 max-w-2xl mx-auto">
                        A gentle note on beauty: every treatment honors your natural features
                        and supports long-term wellness — never trends.
                    </p>
                </div>
            </section>

            {/* CONTROLS */}
            <section className="sticky top-24 z-30 bg-[#F2EDE2]/95 backdrop-blur-xl border-b border-black/10">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-5">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="relative md:max-w-sm w-full">
                            <Search size={16} className="absolute top-1/2 -translate-y-1/2 left-4 text-[#8C7E6A]" />
                            <input
                                data-testid="services-search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search treatments..."
                                className="w-full bg-white border border-black/10 rounded-full pl-11 pr-5 py-3 text-sm focus:outline-none focus:border-[#262422] transition"
                            />
                        </div>
                        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCat(c)}
                                    data-testid={`cat-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                                    className={`shrink-0 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.2em] font-medium border transition ${
                                        activeCat === c
                                            ? "bg-[#262422] text-[#A89070] border-[#A89070]"
                                            : "border-black/15 text-[#544638] hover:border-[#262422]"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* LIST */}
            <section className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
                {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat} className="mb-20 last:mb-0">
                        {activeCat === "All" && (
                            <div className="flex items-end justify-between mb-10">
                                <div>
                                    <div className="eyebrow eyebrow-gold mb-3">{cat}</div>
                                    <h2 className="serif text-4xl md:text-5xl">{items.length} treatment{items.length !== 1 ? "s" : ""}</h2>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 lg:gap-x-10">
                            {items.map((s, i) => (
                                <motion.div
                                    key={s.slug}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: (i % 9) * 0.04 }}
                                >
                                    <Link
                                        to={`/services/${s.slug}`}
                                        data-testid={`service-card-${s.slug}`}
                                        className="group block pt-2"
                                    >
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <div className="eyebrow text-[#8C7E6A]">{s.category}</div>
                                            <div className="eyebrow opacity-50">SKU {s.sku}</div>
                                        </div>
                                        <h3 className="serif text-3xl md:text-4xl leading-tight group-hover:text-[#262422] transition">
                                            {s.name}
                                        </h3>
                                        <p className="mt-3 text-[#544638] line-clamp-2 min-h-[48px]">{s.short_desc}</p>
                                        <hr className="my-5 border-[#A89070]/10" />
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="text-2xl serif">{fmtPrice(s.price)}</span>
                                            <span className="inline-flex items-center gap-2 bg-[#262422] text-[#F2EDE2] group-hover:bg-[#A89070] group-hover:text-white rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] font-semibold transition">
                                                Book <ArrowRight size={13} />
                                            </span>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                ))}

                {filtered.length === 0 && (
                    <div className="text-center py-32 text-[#8C7E6A]">No treatments match your search.</div>
                )}
            </section>
        </div>
    );
}
